import type { EmployeeContactDetailsDto } from "@/generated/model/employeeContactDetailsDto";
import { useUpdateEmployeeContactDetails } from "@/generated/people/people";
import {
  useProfileSectionForm,
  useProfileSectionSave,
} from "@/hooks/use-profile-section-form";
import {
  type ContactDetailsFormValues,
  contactDetailsFormValues,
  contactDetailsRequest,
  contactDetailsSchema,
} from "@/lib/people-profile-sections";

import { ProfileSectionForm } from "../ProfileSectionForm";
import { SectionTextField } from "./ProfileSectionFields";

const FIELDS: {
  key: keyof ContactDetailsFormValues;
  label: string;
  type: "tel" | "email";
}[] = [
  { key: "mobile", label: "Mobile", type: "tel" },
  { key: "secondaryMobile", label: "Secondary mobile", type: "tel" },
  { key: "homePhone", label: "Home phone", type: "tel" },
  { key: "personalEmail", label: "Personal email", type: "email" },
  { key: "workEmail", label: "Work email", type: "email" },
];

interface EditContactDetailsFormProps {
  nurseryId: string;
  userId: string;
  contactDetails: EmployeeContactDetailsDto;
  onDone: () => void;
}

export function EditContactDetailsForm({
  nurseryId,
  userId,
  contactDetails,
  onDone,
}: EditContactDetailsFormProps) {
  const { values, setErrors, setField, errors, validate } =
    useProfileSectionForm(
      () => contactDetailsFormValues(contactDetails),
      contactDetailsSchema,
    );
  const update = useUpdateEmployeeContactDetails({
    mutation: useProfileSectionSave(
      nurseryId,
      userId,
      "Contact details",
      onDone,
      setErrors,
    ),
  });
  const isPending = update.isPending;

  const handleSubmit = () => {
    if (!validate()) return;
    update.mutate({ nurseryId, userId, data: contactDetailsRequest(values) });
  };

  return (
    <ProfileSectionForm
      testId="edit-contact-details-form"
      onCancel={onDone}
      isPending={isPending}
      onSubmit={handleSubmit}
    >
      {FIELDS.map(({ key, label, type }) => (
        <SectionTextField
          key={key}
          type={type}
          label={label}
          name={key}
          value={values[key]}
          onChange={(value) => setField(key, value)}
          disabled={isPending}
          error={errors[key]}
        />
      ))}
    </ProfileSectionForm>
  );
}
